import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import ThemePicker from '../components/ThemePicker';
import '../styles/Dashboard.css';

const ProviderDashboard = () => {
  const { user } = useAuth();
  const [provider, setProvider] = useState(null);
  const [agents, setAgents] = useState([]);
  const [accessHistory, setAccessHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('agents');
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [qrCodeId, setQrCodeId] = useState('');
  const [patientData, setPatientData] = useState(null);
  const [newAgent, setNewAgent] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });

  useEffect(() => {
    loadProviderData();
    loadAccessHistory();
  }, []);

  const loadProviderData = async () => {
    try {
      const res = await api.get('/api/providers/profile');
      if (res.data.provider) {
        setProvider(res.data.provider);
        setAgents(res.data.provider.agents || []);
      }
    } catch (error) {
      toast.error('Failed to load provider data');
    }
  };

  const loadAccessHistory = async () => {
    try {
      const res = await api.get('/api/providers/access-history');
      setAccessHistory(res.data.accessLogs);
    } catch (error) {
      console.error('Failed to load access history');
    }
  };

  const handleAgentChange = (e) => {
    setNewAgent({ ...newAgent, [e.target.name]: e.target.value });
  };

  const createAgent = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/providers/agents', newAgent);
      toast.success('Agent created successfully');
      setShowAgentForm(false);
      setNewAgent({ firstName: '', lastName: '', email: '', password: '' });
      loadProviderData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create agent');
    }
  };

  const toggleAgentStatus = async (agentId, currentStatus) => {
    try {
      await api.put(`/api/providers/agents/${agentId}`, {
        isActive: !currentStatus
      });
      toast.success('Agent status updated');
      loadProviderData();
    } catch (error) {
      toast.error('Failed to update agent status');
    }
  };

  const deleteAgent = async (agentId) => {
    if (window.confirm('Are you sure you want to delete this agent?')) {
      try {
        await api.delete(`/api/providers/agents/${agentId}`);
        toast.success('Agent deleted successfully');
        loadProviderData();
      } catch (error) {
        toast.error('Failed to delete agent');
      }
    }
  };

  const accessPatientByQR = async (e) => {
    e.preventDefault();
    try {
      const res = await api.get(`/api/users/qr/${qrCodeId}`);
      setPatientData(res.data.user);
      toast.success('Individual information retrieved');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to retrieve individual data');
      setPatientData(null);
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>
          {user?.role === 'provider' 
            ? provider?.organizationName 
            : `${user?.firstName} ${user?.lastName} - ${user?.organizationName}`}
        </h1>
        <p className="user-email">{user?.email}</p>
        {user?.role === 'agent' && (
          <span className="badge badge-agent">Agent Account</span>
        )}
      </div>

      <div className="dashboard-tabs">
        <button 
          className={activeTab === 'agents' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('agents')}
        >
          {user?.role === 'provider' ? 'Manage Agents' : 'QR Access'}
        </button>
        <button 
          className={activeTab === 'qr' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('qr')}
        >
          Access Individual
        </button>
        <button
          className={activeTab === 'access' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('access')}
        >
          Access History
        </button>
        <button
          className={activeTab === 'appearance' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('appearance')}
        >
          🎨 Appearance
        </button>
      </div>

      {activeTab === 'agents' && user?.role === 'provider' && (
        <div className="tab-content">
          <div className="section-header">
            <h2>Agent Management</h2>
            <button 
              onClick={() => setShowAgentForm(!showAgentForm)} 
              className="btn btn-primary"
            >
              {showAgentForm ? 'Cancel' : 'Add New Agent'}
            </button>
          </div>

          {showAgentForm && (
            <div className="agent-form-container">
              <h3>Create New Agent</h3>
              <form onSubmit={createAgent} className="agent-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={newAgent.firstName}
                      onChange={handleAgentChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={newAgent.lastName}
                      onChange={handleAgentChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={newAgent.email}
                    onChange={handleAgentChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    name="password"
                    value={newAgent.password}
                    onChange={handleAgentChange}
                    required
                    minLength="8"
                  />
                </div>

                <button type="submit" className="btn btn-success">
                  Create Agent
                </button>
              </form>
            </div>
          )}

          <div className="agents-list">
            <h3>Current Agents ({agents.length})</h3>
            
            {agents.length === 0 ? (
              <p className="no-data">No agents yet. Create your first agent above.</p>
            ) : (
              <div className="agents-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Agent ID</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((agent) => (
                      <tr key={agent._id}>
                        <td>{agent.firstName} {agent.lastName}</td>
                        <td>{agent.email}</td>
                        <td><code>{agent.agentId}</code></td>
                        <td>
                          <span className={`status-badge ${agent.isActive ? 'active' : 'inactive'}`}>
                            {agent.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{new Date(agent.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button
                            onClick={() => toggleAgentStatus(agent._id, agent.isActive)}
                            className="btn btn-sm btn-secondary"
                          >
                            {agent.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => deleteAgent(agent._id)}
                            className="btn btn-sm btn-danger"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="provider-info">
            <h3>Provider Information</h3>
            <p><strong>Provider ID:</strong> <code>{provider?._id}</code></p>
            <p><small>Share this ID with your agents for login</small></p>
          </div>
        </div>
      )}

      {activeTab === 'qr' && (
        <div className="tab-content">
          <div className="qr-access-section">
            <h2>Access Individual Information</h2>
            <p>Enter the individual's QR code ID to access their medical information</p>

            <form onSubmit={accessPatientByQR} className="qr-form">
              <div className="form-group">
                <label>QR Code ID</label>
                <input
                  type="text"
                  value={qrCodeId}
                  onChange={(e) => setQrCodeId(e.target.value)}
                  placeholder="Enter QR code ID"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary">
                Access Individual Data
              </button>
            </form>

            {patientData && (
              <div className="patient-info-display">
                <h3>Individual Information</h3>
                <div className="patient-details">
                  <p><strong>Name:</strong> {patientData.firstName} {patientData.lastName}</p>
                  
                  <h4>Medical Information</h4>
                  {patientData.medicalInfo && (
                    <>
                      <p><strong>Blood Type:</strong> {patientData.medicalInfo.bloodType || 'Not provided'}</p>
                      <p><strong>Date of Birth:</strong> {patientData.medicalInfo.dateOfBirth ? new Date(patientData.medicalInfo.dateOfBirth).toLocaleDateString() : 'Not provided'}</p>
                      <p><strong>Height:</strong> {patientData.medicalInfo.height || 'Not provided'}</p>
                      <p><strong>Weight:</strong> {patientData.medicalInfo.weight || 'Not provided'}</p>
                      
                      <h5>Allergies:</h5>
                      <ul>
                        {patientData.medicalInfo.allergies?.length > 0 
                          ? patientData.medicalInfo.allergies.map((allergy, idx) => (
                              <li key={idx}>{allergy}</li>
                            ))
                          : <li>None reported</li>
                        }
                      </ul>

                      <h5>Current Medications:</h5>
                      <ul>
                        {patientData.medicalInfo.medications?.length > 0 
                          ? patientData.medicalInfo.medications.map((med, idx) => (
                              <li key={idx}>{med}</li>
                            ))
                          : <li>None reported</li>
                        }
                      </ul>

                      <h5>Medical Conditions:</h5>
                      <ul>
                        {patientData.medicalInfo.conditions?.length > 0 
                          ? patientData.medicalInfo.conditions.map((condition, idx) => (
                              <li key={idx}>{condition}</li>
                            ))
                          : <li>None reported</li>
                        }
                      </ul>

                      {patientData.medicalInfo.emergencyContact && (
                        <>
                          <h5>Emergency Contact:</h5>
                          <p><strong>Name:</strong> {patientData.medicalInfo.emergencyContact.name || 'Not provided'}</p>
                          <p><strong>Phone:</strong> {patientData.medicalInfo.emergencyContact.phone || 'Not provided'}</p>
                          <p><strong>Relationship:</strong> {patientData.medicalInfo.emergencyContact.relationship || 'Not provided'}</p>
                        </>
                      )}

                      {patientData.medicalInfo.notes && (
                        <>
                          <h5>Additional Notes:</h5>
                          <p>{patientData.medicalInfo.notes}</p>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'access' && (
        <div className="tab-content">
          <div className="access-history-section">
            <h2>Access History</h2>
            <p>View all individual information access by your organization</p>

            {accessHistory.length === 0 ? (
              <p className="no-data">No access history yet</p>
            ) : (
              <div className="access-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Individual</th>
                      <th>Agent</th>
                      <th>Access Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessHistory.map((log) => (
                      <tr key={log._id}>
                        <td>{new Date(log.timestamp).toLocaleString()}</td>
                        <td>
                          {log.userId?.firstName} {log.userId?.lastName}
                        </td>
                        <td>{log.agentName}</td>
                        <td>
                          <span className={`access-badge ${log.accessType}`}>
                            {log.accessType}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'appearance' && (
        <div className="tab-content">
          <ThemePicker scope="dashboard" />
        </div>
      )}
    </div>
  );
};

export default ProviderDashboard;
